import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { resolveImg } from "../lib/img";
import SEO, { breadcrumbLD, SITE_URL } from "../components/SEO";

/**
 * Lightweight markdown-ish renderer for article body.
 * Supported block syntaxes (separated by blank lines):
 *   ## Section heading
 *   ### Sub heading
 *   > Quote text — Attribution
 *   ![Caption text](/api/static/img/xxx.jpg)
 *   --- (horizontal rule)
 *   Paragraph of text with **bold** and *italic*.
 */
function renderInline(text) {
    // Split on ** first, then *; then return JSX
    const bold = text.split(/(\*\*[^*]+\*\*)/g);
    return bold.map((seg, i) => {
        if (seg.startsWith("**") && seg.endsWith("**")) {
            return <strong key={i} className="font-normal text-cp-foret">{seg.slice(2, -2)}</strong>;
        }
        // italic
        const it = seg.split(/(\*[^*]+\*)/g);
        return it.map((s, j) => {
            if (s.startsWith("*") && s.endsWith("*") && s.length > 2) {
                return <em key={`${i}-${j}`} className="cp-display-italic">{s.slice(1, -1)}</em>;
            }
            return <span key={`${i}-${j}`}>{s}</span>;
        });
    });
}

function Block({ block }) {
    const t = block.trim();
    if (!t) return null;
    if (t.startsWith("## ")) {
        return (
            <h2 className="cp-serif text-3xl md:text-4xl leading-tight text-cp-encre mt-16 mb-6 tracking-[-0.01em]">
                {renderInline(t.slice(3))}
            </h2>
        );
    }
    if (t.startsWith("### ")) {
        return (
            <h3 className="cp-serif italic text-2xl md:text-3xl text-cp-foret mt-12 mb-4 cp-display-italic">
                {renderInline(t.slice(4))}
            </h3>
        );
    }
    if (t.startsWith("> ")) {
        return (
            <blockquote className="cp-serif italic text-2xl md:text-3xl text-cp-encre border-l-2 border-cp-foret pl-6 md:pl-10 my-10 leading-snug">
                {renderInline(t.slice(2))}
            </blockquote>
        );
    }
    if (t === "---") {
        return <hr className="border-cp-border my-12 max-w-[200px]" />;
    }
    // ![alt](src) — optionally followed by a second line for caption annotation
    const imgMatch = t.match(/^!\[([^\]]*)\]\(([^)]+)\)(?:\n+([\s\S]*))?$/);
    if (imgMatch) {
        const [, alt, src, extra] = imgMatch;
        return (
            <figure className="my-12 -mx-4 md:-mx-16 lg:-mx-24">
                <div className="aspect-[16/10] overflow-hidden bg-cp-craie">
                    <img
                        src={resolveImg(src)}
                        alt={alt}
                        className="w-full h-full object-cover cp-photo-real"
                        loading="lazy"
                    />
                </div>
                {alt && (
                    <figcaption className="cp-tiny text-cp-gris mt-4 px-4 md:px-0">
                        {alt}{extra ? ` — ${extra.trim()}` : ""}
                    </figcaption>
                )}
            </figure>
        );
    }
    return (
        <p className="text-cp-encre text-lg md:text-xl font-light leading-[1.7] mb-7 whitespace-pre-line">
            {renderInline(t)}
        </p>
    );
}

export default function ArticleDetail() {
    const { slug } = useParams();
    const [article, setArticle] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        setArticle(null);
        setError("");
        api.get(`/articles/${slug}`).then((r) => setArticle(r.data)).catch(() => setError("Article introuvable."));
    }, [slug]);

    if (error) return <div className="pt-40 text-center pb-40"><h1 className="cp-serif text-5xl">{error}</h1><Link to="/magazine" className="cp-btn-ghost mt-10 inline-flex">Retour</Link></div>;
    if (!article) return <div className="pt-40 text-center text-cp-gris">Chargement…</div>;

    const blocks = (article.content || "").split(/\n\s*\n/);

    const articleLD = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": article.title,
        "description": article.excerpt || article.subtitle || "",
        "image": article.cover_image ? (article.cover_image.startsWith("http") ? article.cover_image : `${SITE_URL}${article.cover_image}`) : undefined,
        "datePublished": article.published_at || article.created_at,
        "dateModified": article.updated_at || article.published_at || article.created_at,
        "author": {
            "@type": "Organization",
            "name": article.author || "Côte Perdue",
        },
        "publisher": {
            "@type": "Organization",
            "name": "Côte Perdue",
            "logo": {"@type": "ImageObject", "url": `${SITE_URL}/logo512.png`},
        },
        "mainEntityOfPage": `${SITE_URL}/magazine/${slug}`,
        "articleSection": article.category || "magazine",
        "keywords": (article.tags || []).join(", "),
        "inLanguage": "fr-FR",
    };

    return (
        <article className="pt-28" data-testid={`article-detail-${slug}`}>
            <SEO
                title={article.title}
                description={article.excerpt || article.subtitle}
                image={article.cover_image}
                path={`/magazine/${slug}`}
                type="article"
                keywords={article.tags || []}
                structuredData={[
                    articleLD,
                    breadcrumbLD([
                        { name: "Accueil", path: "/" },
                        { name: "Magazine", path: "/magazine" },
                        { name: article.title, path: `/magazine/${slug}` },
                    ]),
                ]}
            />

            <section className="px-6 md:px-12 max-w-[1100px] mx-auto pt-10 md:pt-16 pb-10">
                <Link to="/magazine" className="cp-kicker text-cp-gris hover:text-cp-foret flex items-center gap-2 mb-10">
                    <ArrowLeft size={14} strokeWidth={1.5} /> Magazine
                </Link>
                <p className="cp-kicker text-cp-foret mb-6">{article.category} · {article.reading_time} min de lecture</p>
                <h1 className="cp-serif text-4xl sm:text-5xl md:text-6xl lg:text-[72px] leading-[1.02] text-cp-noir mb-8 tracking-[-0.02em]">
                    {article.title}
                </h1>
                <p className="cp-serif text-xl md:text-2xl italic font-light text-cp-gris leading-snug max-w-3xl">{article.subtitle}</p>
                <p className="cp-kicker text-cp-gris mt-8">Par {article.author}</p>
            </section>

            <section className="px-6 md:px-12 max-w-[1400px] mx-auto mb-16">
                <div className="aspect-[16/9] overflow-hidden bg-cp-craie">
                    <img src={resolveImg(article.cover_image)} alt={article.title} className="w-full h-full object-cover cp-photo-real" />
                </div>
            </section>

            <section className="px-6 md:px-12 max-w-[780px] mx-auto pb-32" data-testid="article-body">
                {blocks.map((b, i) => <Block key={i} block={b} />)}
            </section>
        </article>
    );
}
